import { booleanAttribute, ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { DsButtonModule, DsMessageModule } from '@bmw-ds/components';

import { AddressField } from '../../../customer/model/address-field.model';
import { VehicleField } from '../../../customer/model/vehicle-field.model';
import { AddressViewService } from '../../../customer/services/address.view-service';
import { CustomerService } from '../../../customer/services/customer.service';
import { FeatureVisibilityService } from '../../../customer/services/feature-visibility.service';
import { useI18n } from '../../../i18n/services/i18n.service';
import { SearchFormService } from '../../search-form.service';
import { CustomerSearchMessageComponent } from '../customer-search-message/customer-message-search.component';
import { FormSearchForCustomerComponent } from '../form-search-for-customer/form-search-for-customer.component';
import { FormSearchForCustomerAdvancedComponent } from '../form-search-for-customer-advanced/form-search-for-customer-advanced.component';
import { FormSearchForLicenceComponent } from '../form-search-for-licence/form-search-for-licence.component';
import { FormSearchForVinComponent } from '../form-search-for-vin/form-search-for-vin.component';
import { SearchButtonComponent } from '../search-button/search-button.component';

@Component({
  selector: 'cdf-customer-search-container',
  imports: [
    DsButtonModule,
    DsMessageModule,
    FormSearchForCustomerComponent,
    FormSearchForCustomerAdvancedComponent,
    CustomerSearchMessageComponent,
    FormSearchForVinComponent,
    FormSearchForLicenceComponent,
    SearchButtonComponent,
  ],
  host: {
    class: 'd-flex flex-column gap-4x',
    '[style.max-width]': 'fullWidth() ? undefined : "100ch"',
  },
  template: `
    <form (submit)="$event.preventDefault()" class="d-flex flex-column gap-2x">
      <cdf-form-search-for-customer
        [(firstName)]="searchFormService.firstName"
        [(lastName)]="searchFormService.lastName"
        [(suffix)]="searchFormService.suffix"
        [(bmwMiniId)]="searchFormService.bmwMiniId"
        [(mobilePhone)]="searchFormService.mobilePhone"
        [(phone)]="searchFormService.phone"
        [(taxNumber)]="searchFormService.taxNumber"
        (searchCustomer)="searchFormService.searchCustomers()"
      />
      <cdf-form-advanced-search
        [(address)]="searchFormService.address"
        [availableCountries]="customerService.countries()"
        [(isAdvancedSearchExpanded)]="searchFormService.isAdvancedSearchExpanded"
        [isAMainDataFieldFilled]="searchFormService.isAMainDataFieldFilled()"
        [addressUiFormat]="addressUiFormat()"
      >
        <ds-button
          variant="ghost"
          icon="refresh"
          [disabled]="searchFormService.anyRequestsInProgress() || !searchFormService.isAnyDataFieldFilled()"
          (click)="searchFormService.resetForm()"
        >
          {{ i18n('button.reset') }}
        </ds-button>
        <cdf-search-button
          id="search-customer-button"
          [label]="i18n('customer')"
          [disabled]="
            !searchFormService.isMobilePhoneValid() ||
            !searchFormService.isPhoneValid() ||
            !searchFormService.isAMainDataFieldFilled()
          "
          [isLoading]="searchFormService.anyRequestsInProgress()"
          (searchClick)="searchFormService.searchCustomers()"
        />
      </cdf-form-advanced-search>
    </form>

    @if (showVehicleSearchSourceHint()) {
      <ds-message variant="slim" tone="info" [messageText]="i18n('customerSearch.vehicleSearchRetailOnly')" />
    }

    @if (!hideVinSearch() && !featureVisibilityService.isHidden(VehicleField.VIN_SEARCH)) {
      <form (submit)="$event.preventDefault()" class="d-flex flex-column gap-6x">
        <cdf-form-search-for-vin
          [(vin)]="searchFormService.vin"
          [saving]="searchFormService.anyRequestsInProgress()"
          (searchVIN)="searchFormService.searchVIN()"
        />
      </form>
    }

    @if (!hideLicenseSearch() && !featureVisibilityService.isHidden(VehicleField.LICENSE_SEARCH)) {
      <form (submit)="$event.preventDefault()" class="d-flex flex-column gap-6x">
        <cdf-form-search-for-licence
          [(licenceNumber)]="searchFormService.licenceNumber"
          [saving]="searchFormService.anyRequestsInProgress()"
          (searchLicence)="searchFormService.searchLicence()"
        />
      </form>
    }

    <cdf-customer-search-message
      [failedRequests]="searchFormService.failedRequests()"
      [attemptedRequests]="searchFormService.attemptedRequests()"
      [warnings]="searchFormService.requestWarnings()"
    />
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerSearchContainer {
  protected readonly VehicleField = VehicleField;
  protected readonly i18n = useI18n();
  protected readonly searchFormService = inject(SearchFormService);
  protected readonly customerService = inject(CustomerService);
  protected readonly addressViewService = inject(AddressViewService);
  protected readonly featureVisibilityService = inject(FeatureVisibilityService);
  /** Only worth saying when a vehicle search is actually offered. */
  protected readonly showVehicleSearchSourceHint = computed(
    () =>
      this.searchFormService.vehicleSearchIsRetailOnly() &&
      ((!this.hideVinSearch() && !this.featureVisibilityService.isHidden(VehicleField.VIN_SEARCH)) ||
        (!this.hideLicenseSearch() && !this.featureVisibilityService.isHidden(VehicleField.LICENSE_SEARCH))),
  );

  readonly hideVinSearch = input(false, { transform: booleanAttribute });
  readonly hideLicenseSearch = input(false, { transform: booleanAttribute });
  readonly fullWidth = input(false, { transform: booleanAttribute });

  readonly addressUiFormat = computed<AddressField[][]>(() => {
    const market = this.searchFormService.address().country ?? '';
    return this.addressViewService.getAdvancedSearchUIFormatForMarket(market);
  });
}
